package com.evoapps.evoshift.widgets

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.text.selection.LocalTextSelectionColors
import androidx.compose.foundation.text.selection.TextSelectionColors
import androidx.compose.material3.ColorScheme
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import com.evoapps.evoshift.domain.model.Appearance
import com.evoapps.evoshift.ui.AppColorScheme
import com.evoapps.evoshift.ui.Cream
import com.evoapps.evoshift.ui.DarkMetallicBackgroundBrush
import com.evoapps.evoshift.ui.PerAppearance
import com.evoapps.evoshift.ui.ThemePalette

/*
 * The colours text and icons take on the surface they sit on. The app's theme is light (navy on
 * cream), but its dialogs, sheets and popovers are navy: Material's defaults there (onSurface,
 * primary, error, a TextButton's navy) vanished on the navy. [ProvideDialogColors] gives everything
 * inside a dialog surface light defaults and its own colour scheme, and [LocalOnColours] the roles
 * for colours a composable sets itself.
 *
 * Every role is at least 4.5:1 against the dialog's navy gradient (DeepNavy to ReflectiveBlue) and
 * against the cream cards (DialogContrastTest checks the dialogs on a phone). SH-196: "navy" and
 * "gold" are the base and the accent the person picked (Palette.kt); ThemeContrastTest checks the
 * roles on all nine combinations.
 */

/** What text and icons use on the surface they are drawn on. */
@Immutable
data class OnColours(
    /** Body text and icons. */
    val onSurface: Color,
    /** Secondary lines: descriptions, hints, a subtitle. */
    val onSurfaceVariant: Color,
    /** Errors and warnings in red. */
    val error: Color,
    /** A "be careful" amber line. */
    val warning: Color,
    /** "Fine" in green. */
    val success: Color,
    /** Links. */
    val link: Color,
    /** The gold accent (a call to action, a highlighted figure): light gold on navy, a deep gold on cream. */
    val accent: Color,
    /** True on the navy dialog surfaces. */
    val dark: Boolean
)

private fun onDialog(p: ThemePalette) = OnColours(
    onSurface = Color.White,
    onSurfaceVariant = p.base.onDarkVariant,
    error = ErrorOnNavy,
    warning = Color(0xFFFFCC80),
    success = Color(0xFFA5D6A7),
    link = p.accent.bright,
    accent = p.accent.bright,
    dark = true
)

private fun onScreen(p: ThemePalette) = OnColours(
    onSurface = p.base.base,
    onSurfaceVariant = p.base.onLightVariant,
    error = Color(0xFFBA1A1A),
    warning = Color(0xFF8A4B00),
    success = Color(0xFF1B6E20),
    link = p.base.base,
    accent = p.accent.deep,
    dark = false
)

private val OnDialogs = PerAppearance(::onDialog)
private val OnScreens = PerAppearance(::onScreen)

/** On the navy dialogs, sheets and popovers (SH-196: the base's and the accent's on show). */
val OnDialog: OnColours get() = OnDialogs.current

/** On the cream screens and cards. */
val OnScreen: OnColours get() = OnScreens.current

/**
 * SH-245: on the brand gradient of the full screens (DarkMetallicBackgroundBrush: sign in and
 * sign up, an invitation, onboarding, the paywall, the kiosk). It runs from the base to its
 * reflective step, the dialogs' gradient, so its text takes the dialogs' roles: white, the light
 * variant, the bright accent. The base (DeepNavy, the cream screens' text) vanished there.
 */
val OnGradient: OnColours get() = OnDialog

/** [OnDialog] / [OnScreen] / [OnGradient] of [appearance] (tests). */
fun onDialogColours(appearance: Appearance): OnColours = OnDialogs.of(appearance)
fun onScreenColours(appearance: Appearance): OnColours = OnScreens.of(appearance)
fun onGradientColours(appearance: Appearance): OnColours = onDialogColours(appearance)

/** The roles of the surface being drawn on: [OnDialog] inside [ProvideDialogColors], else [OnScreen] (AppTheme provides it). */
val LocalOnColours = staticCompositionLocalOf { onScreenColours(Appearance.DEFAULT) }

/** Material's colours on a navy dialog: light text, gold accents, a red that reads on navy. */
private fun dialogScheme(p: ThemePalette) = darkColorScheme(
    primary = p.accent.bright,
    onPrimary = p.base.base,
    primaryContainer = p.base.containerHigh,
    onPrimaryContainer = Color.White,
    inversePrimary = p.base.base,
    secondary = p.accent.metallic,
    onSecondary = p.base.base,
    secondaryContainer = p.accent.metallic,
    onSecondaryContainer = p.base.base,
    tertiary = p.accent.bright,
    onTertiary = p.base.base,
    tertiaryContainer = p.base.containerHigh,
    onTertiaryContainer = Color.White,
    background = p.base.base,
    onBackground = Color.White,
    surface = p.base.base,
    onSurface = Color.White,
    surfaceVariant = p.base.containerHigh,
    onSurfaceVariant = p.base.onDarkVariant,
    surfaceTint = p.accent.bright,
    inverseSurface = Cream,
    inverseOnSurface = p.base.base,
    error = ErrorOnNavy,
    onError = p.base.base,
    errorContainer = Color(0xFF5C1A1A),
    onErrorContainer = Color(0xFFFFDAD6),
    outline = p.base.outline,
    outlineVariant = p.base.outlineVariant,
    scrim = Color.Black,
    surfaceBright = p.base.containerHigh,
    surfaceDim = p.base.base,
    surfaceContainerLowest = p.base.base,
    surfaceContainerLow = p.base.container,
    surfaceContainer = p.base.container,
    surfaceContainerHigh = p.base.container,
    surfaceContainerHighest = p.base.containerHigh
)

private val DialogSchemes = PerAppearance(::dialogScheme)

/** The navy dialogs' colour scheme of the appearance on show. */
val DialogColorScheme: ColorScheme get() = DialogSchemes.current

/** The dialogs' colour scheme of [appearance] (tests, previews). */
fun dialogColorScheme(appearance: Appearance): ColorScheme = DialogSchemes.of(appearance)

private val DialogSelectionColors = PerAppearance { p ->
    TextSelectionColors(handleColor = p.accent.bright, backgroundColor = p.accent.metallic.copy(alpha = 0.45f))
}

/**
 * Content on a navy dialog surface: white text and icons by default, Material components in
 * [DialogColorScheme] (a TextButton gold, a checkbox gold with a navy tick, a menu navy), and
 * [LocalOnColours] = [OnDialog].
 */
@Composable
fun ProvideDialogColors(content: @Composable () -> Unit) {
    MaterialTheme(colorScheme = DialogColorScheme, typography = MaterialTheme.typography, shapes = MaterialTheme.shapes) {
        CompositionLocalProvider(
            LocalContentColor provides OnDialog.onSurface,
            LocalOnColours provides OnDialog,
            LocalTextSelectionColors provides DialogSelectionColors.current,
            content = content
        )
    }
}

/**
 * SH-245: content drawn straight on the brand gradient (a full screen's
 * DarkMetallicBackgroundBrush): text and icons white by default and [LocalOnColours] =
 * [OnGradient], so a text that sets no colour, or takes a role, reads there. Material's colour
 * scheme stays the app's (the screens style their fields and buttons themselves); the cards on
 * the gradient bring their own colours (StandoutCard, RegularCard).
 */
@Composable
fun ProvideGradientColors(content: @Composable () -> Unit) {
    CompositionLocalProvider(
        LocalContentColor provides OnGradient.onSurface,
        LocalOnColours provides OnGradient,
        content = content
    )
}

/**
 * SH-245: a full screen on the brand gradient (sign in, onboarding, the paywall, the kiosk): a
 * [Box] painted with DarkMetallicBackgroundBrush under the whole of [modifier] (its padding
 * included), its content in [ProvideGradientColors].
 */
@Composable
fun GradientBox(
    modifier: Modifier = Modifier,
    contentAlignment: Alignment = Alignment.TopStart,
    content: @Composable BoxScope.() -> Unit
) {
    Box(Modifier.background(DarkMetallicBackgroundBrush).then(modifier), contentAlignment = contentAlignment) {
        ProvideGradientColors { content() }
    }
}

/**
 * A light (cream or white) panel or card, e.g. inside a navy dialog: back to the app's own
 * colours, so its text is navy, its buttons and chips the screen's and [LocalOnColours] =
 * [OnScreen]. Harmless on a screen, which has these colours already.
 */
@Composable
fun ProvideScreenColors(content: @Composable () -> Unit) {
    MaterialTheme(colorScheme = AppColorScheme, typography = MaterialTheme.typography, shapes = MaterialTheme.shapes) {
        CompositionLocalProvider(
            LocalContentColor provides OnScreen.onSurface,
            LocalOnColours provides OnScreen,
            content = content
        )
    }
}
